// Bannerin käyttöliittymä: ensimmäinen taso, toinen taso (kategoriavalinnat)
// ja tallennusvahvistus todistustunnuksineen. DOM-rakenne ja tyylit on
// portattu vaihe 0:n edge-funktiosta sellaisinaan (käyttäytyminen identtinen).

import type { BannerColors, BannerTexts, CategoryConfig, Choices, CmpConfig, StoredConsent } from './types';

/** UI:n tarvitsemat riippuvuudet — orkestrointi (cmp.ts) kytkee nämä. */
export interface BannerUiContext {
  config: CmpConfig;
  lang: string;
  getStoredConsent: () => StoredConsent | null;
  getConsentId: () => string | null;
  storeConsent: (choices: Choices, action: string) => void;
  applyConsent: (choices: Choices) => void;
  sendConsent: (choices: Choices, action: string, onDone?: (consentId: string) => void) => void;
}

/** Luo elementin, asettaa inline-tyylit ja tekstisisällön. */
export function createEl(tag: string, styles?: Partial<CSSStyleDeclaration>, text?: string): HTMLElement {
  const el = document.createElement(tag) as HTMLElement;
  if (styles) Object.assign(el.style, styles);
  if (text) el.textContent = text;
  return el;
}

/** Tallennusvahvistus: todistustunnus + kopiointi. Sulkeutuu 10 sekunnissa. */
export function showConfirmation(ctx: BannerUiContext, consentId: string): void {
  const overlay = document.getElementById('cmp-overlay');
  if (!overlay) return;
  const c: BannerColors = ctx.config.colors || {};

  overlay.innerHTML = '';
  const box = createEl('div', {
    background: c.background || '#fff', color: c.text || '#1a1a1a', borderRadius: '12px',
    padding: '24px', maxWidth: '480px', width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', fontSize: '14px',
    textAlign: 'center',
  });

  box.appendChild(createEl('div', { fontSize: '28px', marginBottom: '12px' }, '✓'));
  box.appendChild(createEl('h2', { margin: '0 0 8px', fontSize: '18px', fontWeight: '700' }, 'Suostumuksesi on tallennettu'));
  box.appendChild(createEl('p', { margin: '0 0 16px', lineHeight: '1.5', opacity: '0.7', fontSize: '13px' }, 'Voit käyttää alla olevaa todistustunnusta todisteena suostumuksestasi.'));

  const idBox = createEl('div', {
    background: (c.text || '#1a1a1a') + '0a', borderRadius: '8px', padding: '12px',
    fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all', marginBottom: '16px',
    border: '1px solid ' + (c.divider || '#e5e7eb'),
  }, consentId);
  box.appendChild(idBox);

  const btnRow = createEl('div', { display: 'flex', gap: '8px', justifyContent: 'center' });
  const copyBtn = createEl('button', {
    padding: '10px 20px', border: '1px solid ' + (c.divider || '#e5e7eb'), borderRadius: '6px',
    background: 'transparent', color: c.text || '#1a1a1a', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
  }, 'Kopioi tunnus') as HTMLButtonElement;
  copyBtn.onclick = function () {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(consentId).then(function () { copyBtn.textContent = 'Kopioitu!'; });
    } else {
      const ta = document.createElement('textarea'); ta.value = consentId; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
      copyBtn.textContent = 'Kopioitu!';
    }
  };
  const closeBtn = createEl('button', {
    padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontWeight: '600', fontSize: '14px', background: c.acceptButtonBg || '#16a34a', color: c.acceptButtonText || '#fff',
  }, 'Sulje') as HTMLButtonElement;
  closeBtn.onclick = function () { overlay.remove(); };
  btnRow.appendChild(copyBtn);
  btnRow.appendChild(closeBtn);
  box.appendChild(btnRow);

  overlay.appendChild(box);

  setTimeout(function () {
    const el = document.getElementById('cmp-overlay');
    if (el) el.remove();
  }, 10000);
}

/** Bannerin ensimmäinen taso: otsikko, kuvaus ja toimintopainikkeet. */
export function renderBanner(ctx: BannerUiContext): void {
  const CONFIG = ctx.config;
  const LANG = ctx.lang;
  const texts = CONFIG.texts[LANG] || CONFIG.texts['fi'] || CONFIG.texts[Object.keys(CONFIG.texts)[0]];
  if (!texts) return;
  const c: BannerColors = CONFIG.colors || {};
  const cats = CONFIG.categories || [];

  const overlay = createEl('div', {
    position: 'fixed', top: '0', left: '0', right: '0', bottom: '0',
    background: c.overlayBg || 'rgba(0,0,0,0.5)', zIndex: '999998', display: 'flex',
    alignItems: CONFIG.position === 'top' ? 'flex-start' : CONFIG.position === 'center' ? 'center' : 'flex-end',
    justifyContent: 'center', padding: '16px',
  });
  overlay.id = 'cmp-overlay';

  const boxWidth = CONFIG.layout_style === 'bar' ? '100%' : CONFIG.layout_style === 'popup' ? '420px' : '640px';
  const box = createEl('div', {
    background: c.background || '#fff', color: c.text || '#1a1a1a', borderRadius: '12px',
    padding: '24px', maxWidth: boxWidth, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', fontSize: '14px',
  });

  const existingConsentId = ctx.getConsentId();
  if (existingConsentId) {
    const idNotice = createEl('div', {
      background: (c.text || '#1a1a1a') + '0a', borderRadius: '6px', padding: '8px 12px',
      fontSize: '12px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px',
      border: '1px solid ' + (c.divider || '#e5e7eb'),
    });
    const short = existingConsentId.length > 16 ? existingConsentId.substring(0, 16) + '...' : existingConsentId;
    idNotice.appendChild(createEl('span', { opacity: '0.7' }, 'Todistustunnus: ' + short));
    const cpBtn = createEl('button', {
      padding: '2px 8px', border: '1px solid ' + (c.divider || '#e5e7eb'), borderRadius: '4px',
      background: 'transparent', color: c.text || '#1a1a1a', cursor: 'pointer', fontSize: '11px',
    }, 'Kopioi') as HTMLButtonElement;
    cpBtn.onclick = function () {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(existingConsentId).then(function () { cpBtn.textContent = 'Kopioitu!'; });
      } else {
        const ta = document.createElement('textarea'); ta.value = existingConsentId; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
        cpBtn.textContent = 'Kopioitu!';
      }
    };
    idNotice.appendChild(cpBtn);
    box.appendChild(idNotice);
  }

  const title = createEl('h2', { margin: '0 0 8px', fontSize: '18px', fontWeight: '700' }, texts.first_layer_title);
  const desc = createEl('p', { margin: '0 0 16px', lineHeight: '1.5', opacity: '0.85' }, texts.first_layer_description);
  box.appendChild(title);
  box.appendChild(desc);

  const btnRow = createEl('div', { display: 'flex', gap: '8px', flexWrap: 'wrap' });

  function makeBtn(label: string | undefined, bg: string, color: string, onClick: () => void): HTMLElement {
    const btn = createEl('button', {
      padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer',
      fontWeight: '600', fontSize: '14px', background: bg, color: color,
    }, label) as HTMLButtonElement;
    btn.onclick = onClick;
    return btn;
  }

  function acceptAll() {
    const choices: Choices = {};
    cats.forEach(function (cat) { choices[cat.slug] = true; });
    ctx.storeConsent(choices, 'accept_all');
    ctx.applyConsent(choices);
    ctx.sendConsent(choices, 'accept_all', function (consentId) { showConfirmation(ctx, consentId); });
  }

  function rejectAll() {
    const choices: Choices = {};
    cats.forEach(function (cat) { choices[cat.slug] = !!cat.is_required; });
    ctx.storeConsent(choices, 'reject_all');
    ctx.applyConsent(choices);
    ctx.sendConsent(choices, 'reject_all', function (consentId) { showConfirmation(ctx, consentId); });
  }

  let _sending = false;
  function disableButtons() {
    _sending = true;
    const btns = overlay.querySelectorAll('button');
    for (let i = 0; i < btns.length; i++) { btns[i].disabled = true; btns[i].style.opacity = '0.6'; }
  }

  btnRow.appendChild(makeBtn(texts.accept_all_button, c.acceptButtonBg || '#16a34a', c.acceptButtonText || '#fff', function () { if (_sending) return; disableButtons(); acceptAll(); }));

  if (CONFIG.show_reject_on_first_layer) {
    btnRow.appendChild(makeBtn(texts.reject_all_button, c.rejectButtonBg || '#e5e7eb', c.rejectButtonText || '#374151', function () { if (_sending) return; disableButtons(); rejectAll(); }));
  }

  btnRow.appendChild(makeBtn(texts.customize_button, c.customizeButtonBg || 'transparent', c.customizeButtonText || '#2563eb', function () {
    showSecondLayer(ctx, overlay, cats, texts, c);
  }));

  box.appendChild(btnRow);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

/** Bannerin toinen taso: kategoriakohtaiset valinnat ja evästelistat. */
export function showSecondLayer(
  ctx: BannerUiContext,
  overlay: HTMLElement,
  cats: CategoryConfig[],
  texts: BannerTexts,
  c: BannerColors,
): void {
  const LANG = ctx.lang;
  overlay.innerHTML = '';
  const box = createEl('div', {
    background: c.background || '#fff', color: c.text || '#1a1a1a', borderRadius: '12px',
    padding: '24px', maxWidth: '640px', width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', fontSize: '14px',
    maxHeight: '80vh', overflowY: 'auto',
  });

  box.appendChild(createEl('h2', { margin: '0 0 8px', fontSize: '18px', fontWeight: '700' }, texts.second_layer_title));
  box.appendChild(createEl('p', { margin: '0 0 16px', lineHeight: '1.5', opacity: '0.85' }, texts.second_layer_description));

  const stored = ctx.getStoredConsent();
  const storedChoices = stored && stored.choices ? stored.choices : null;
  console.log('[CMP] Second layer opened, restoring choices:', storedChoices);
  const states: Choices = {};
  cats.forEach(function (cat) {
    if (cat.is_required) {
      states[cat.slug] = true;
    } else if (storedChoices && Object.prototype.hasOwnProperty.call(storedChoices, cat.slug)) {
      states[cat.slug] = !!storedChoices[cat.slug];
    } else {
      states[cat.slug] = !!cat.is_default_enabled;
    }
    const catTexts = cat.texts[LANG] || cat.texts['fi'] || cat.texts[Object.keys(cat.texts)[0]] || {};

    const row = createEl('div', { padding: '12px 0', borderBottom: '1px solid ' + (c.divider || '#e5e7eb') });
    const header = createEl('div', { display: 'flex', justifyContent: 'space-between', alignItems: 'center' });
    const left = createEl('div', {});
    left.appendChild(createEl('div', { fontWeight: '600' }, catTexts.name || cat.slug));
    left.appendChild(createEl('div', { fontSize: '12px', opacity: '0.7', marginTop: '2px' }, catTexts.description || ''));
    header.appendChild(left);

    if (cat.is_required) {
      header.appendChild(createEl('span', { fontSize: '12px', opacity: '0.5' }, 'Pakollinen'));
    } else {
      const toggle = createEl('button', {
        width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
        background: states[cat.slug] ? (c.toggleOn || '#16a34a') : (c.toggleOff || '#d1d5db'),
        position: 'relative', transition: 'background 0.2s', flexShrink: '0',
      });
      const knob = createEl('div', {
        width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px',
        left: states[cat.slug] ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      });
      toggle.appendChild(knob);
      toggle.onclick = (function (slug: string, toggleEl: HTMLElement, knobEl: HTMLElement) {
        return function () {
          states[slug] = !states[slug];
          toggleEl.style.background = states[slug] ? (c.toggleOn || '#16a34a') : (c.toggleOff || '#d1d5db');
          knobEl.style.left = states[slug] ? '22px' : '2px';
        };
      })(cat.slug, toggle, knob);
      header.appendChild(toggle);
    }
    row.appendChild(header);

    if (cat.cookies && cat.cookies.length > 0) {
      const table = createEl('div', {
        marginTop: '8px', fontSize: '12px', background: (c.text || '#1a1a1a') + '06',
        borderRadius: '6px', padding: '8px', overflow: 'auto',
      });
      const thRow = createEl('div', { display: 'flex', gap: '12px', fontWeight: '600', opacity: '0.6', paddingBottom: '4px', borderBottom: '1px solid ' + (c.divider || '#e5e7eb'), marginBottom: '4px' });
      thRow.appendChild(createEl('div', { minWidth: '100px', flex: '1' }, 'Nimi'));
      thRow.appendChild(createEl('div', { minWidth: '100px', flex: '1' }, 'Tarjoaja'));
      thRow.appendChild(createEl('div', { minWidth: '60px' }, 'Kesto'));
      table.appendChild(thRow);
      cat.cookies.forEach(function (ck) {
        const tr = createEl('div', { display: 'flex', gap: '12px', padding: '3px 0', opacity: '0.85' });
        tr.appendChild(createEl('div', { minWidth: '100px', flex: '1', fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }, ck.name));
        tr.appendChild(createEl('div', { minWidth: '100px', flex: '1' }, ck.provider));
        tr.appendChild(createEl('div', { minWidth: '60px' }, ck.duration));
        table.appendChild(tr);
        const purpose = (ck.purpose && (ck.purpose[LANG] || ck.purpose['fi'])) || '';
        if (purpose) {
          const pRow = createEl('div', { paddingLeft: '0', opacity: '0.6', fontSize: '11px', paddingBottom: '4px' });
          pRow.textContent = purpose;
          table.appendChild(pRow);
        }
      });
      row.appendChild(table);
    }

    box.appendChild(row);
  });

  const btnRow = createEl('div', { display: 'flex', gap: '8px', marginTop: '16px' });
  const saveBtn = createEl('button', {
    padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontWeight: '600', fontSize: '14px', background: c.acceptButtonBg || '#16a34a', color: c.acceptButtonText || '#fff',
  }, texts.save_preferences_button) as HTMLButtonElement;
  let _saving = false;
  saveBtn.onclick = function () {
    if (_saving) return;
    _saving = true;
    saveBtn.disabled = true;
    saveBtn.style.opacity = '0.6';
    ctx.storeConsent(states, 'save_preferences');
    ctx.applyConsent(states);
    ctx.sendConsent(states, 'save_preferences', function (consentId) { showConfirmation(ctx, consentId); });
  };
  btnRow.appendChild(saveBtn);
  box.appendChild(btnRow);

  overlay.appendChild(box);
}
